import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { InvoicePriceSource, Order, Product } from '../types';

interface EnhancedProductData {
  [itemId: string]: Product;
}

interface InvoiceDocumentProps {
  order: Order;
  shipperInfo: {
    name: string;
    company: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phone: string;
    email?: string;
  };
  invoiceNumber: string;
  awbNumber: string;
  productData?: EnhancedProductData;
  convertedPrices?: number[];
  priceSource?: InvoicePriceSource;
}

const styles = StyleSheet.create({
  page: {
    padding: 16,
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 8,
    borderBottom: '1.5pt solid #000',
    paddingBottom: 6,
  },
  title: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  invoiceInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
  },
  infoLabel: {
    fontFamily: 'Helvetica-Bold',
    marginRight: 2,
  },
  partiesSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    borderBottom: '1pt solid #000',
    paddingBottom: 6,
  },
  partyBox: {
    width: '48%',
  },
  partyTitle: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 3,
    textDecoration: 'underline',
  },
  partyText: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    lineHeight: 1.3,
    marginBottom: 1,
  },
  table: {
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#000',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e5e5e5',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingVertical: 3,
    paddingHorizontal: 2,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingVertical: 3,
    paddingLeft: 2,
    minHeight: 20,
  },
  tableHeaderText: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
  },
  tableCell: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    lineHeight: 1.3,
  },
  colDescription: {
    width: '30%',
    paddingRight: 2,
  },
  colHsCode: {
    width: '10%',
    paddingRight: 2,
    textAlign: 'center',
  },
  colOrigin: {
    width: '7%',
    paddingRight: 2,
    textAlign: 'center',
  },
  colQty: {
    width: '7%',
    paddingRight: 2,
    textAlign: 'center',
  },
  colUnitValue: {
    width: '11%',
    paddingRight: 2,
    textAlign: 'right',
  },
  colSubtotal: {
    width: '11%',
    paddingRight: 2,
    textAlign: 'right',
  },
  colCombinedWeight: {
    width: '24%',
    textAlign: 'right',
  },
  summarySection: {
    marginBottom: 8,
    paddingTop: 4,
    borderTop: '1.5pt solid #000',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
    paddingHorizontal: 2,
  },
  summaryLabel: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    width: '65%',
  },
  summaryValue: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    width: '35%',
  },
  declarationSection: {
    marginTop: 6,
    paddingTop: 6,
    borderTop: '1pt solid #000',
  },
  declarationTitle: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 3,
  },
  declarationText: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    lineHeight: 1.4,
    marginBottom: 4,
  },
  signatureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 6,
  },
  signatureBox: {
    width: '48%',
  },
  signatureLabel: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  signatureLine: {
    borderTop: '0.5pt solid #000',
    marginTop: 8,
    paddingTop: 2,
  },
});

export const InvoiceDocument: React.FC<InvoiceDocumentProps> = ({
  order,
  shipperInfo,
  invoiceNumber,
  awbNumber,
  productData = {},
  convertedPrices,
  priceSource = 'order',
}) => {
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return new Date().toLocaleDateString('en-US');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD'): string => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  const normalizeCurrencyValue = (value: number): number => {
    const rounded = Number(value.toFixed(2));
    return rounded > 0 ? rounded : 0.01;
  };

  const getOrderCurrency = (): string => {
    return order?.purchaseCurrency || 'USD';
  };

  const getOrderExchangeRates = () => {
    return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
  };

  const getConvertedItemPriceFromOrder = (usdPrice: number): number => {
    const currency = getOrderCurrency();
    const rates = getOrderExchangeRates();
    let converted = usdPrice;

    if (currency !== 'USD') {
      const rate = rates[currency] || 1;
      converted = Math.ceil(usdPrice * rate);
    }

    return normalizeCurrencyValue(converted);
  };

  const isJamaicanPricing = priceSource === 'jamaican';

  const getCurrency = (): string => {
    return isJamaicanPricing ? 'JMD' : getOrderCurrency();
  };

  const extractSetQuantity = (name: string): number => {
    // Match patterns like "(Set of 12)", "(Set of 6)", etc.
    const setMatch = name.match(/\(Set of (\d+)\)/i);
    if (setMatch) {
      return parseInt(setMatch[1], 10);
    }
    return 1; // Default to 1 if no set quantity found
  };

  const cleanProductName = (name: string): string => {
    // Remove "(Set of ##)" from the product name
    return name.replace(/\s*\(Set of \d+\)\s*/gi, '').trim();
  };

  const getEffectiveQuantity = (item: typeof order.items[0]): number => {
    const setQuantity = extractSetQuantity(item.name);
    return item.quantity * setQuantity;
  };

  const getItemWeight = (item: typeof order.items[0]): number => {
    const product = productData[item.id];
    const setQuantity = extractSetQuantity(item.name);
    
    // For set items, use only the order quantity (not multiplied by set quantity)
    // For regular items, use order quantity
    const quantityForWeight = item.quantity;
    
    // Use product's actual netWeight if available
    if (product?.netWeight && product?.weightUnit) {
      const weightInGrams = convertWeightToGrams(product.netWeight, product.weightUnit);
      const weightPerUnitKg = weightInGrams / 1000;
      const totalWeightKg = weightPerUnitKg * quantityForWeight;
      return totalWeightKg;
    }
    
    // Fallback to listingQuantity
    const weightInGrams = item.listingQuantity || 56;
    const weightPerUnitKg = weightInGrams / 1000;
    const totalWeightKg = weightPerUnitKg * quantityForWeight;
    return totalWeightKg;
  };

  const convertWeightToGrams = (weight: number, unit: string): number => {
    switch (unit.toLowerCase()) {
      case 'kg':
        return weight * 1000;
      case 'oz':
        return weight * 28.3495;
      case 'lb':
        return weight * 453.592;
      case 'ml':
      case 'g':
      default:
        return weight;
    }
  };

  const getBrandName = (item: typeof order.items[0]): string => {
    const product = productData[item.id];
    
    // Use actual brand from product if available
    if (product?.brand) {
      return product.brand.toUpperCase();
    }
    
    // Fallback: extract from cleaned name (without Set of ##)
    const cleanedName = cleanProductName(item.name);
    const parts = cleanedName.split(/[-–]/);
    if (parts.length > 1) {
      return parts[0].trim().toUpperCase();
    }
    const firstWord = cleanedName.split(' ')[0];
    return firstWord.toUpperCase();
  };

  const getProductWeight = (item: typeof order.items[0]): string => {
    const product = productData[item.id];
    
    // Use actual weight from product if available
    if (product?.netWeight && product?.weightUnit) {
      return `${product.netWeight}${product.weightUnit.toLowerCase()}`;
    }
    
    // Fallback: extract from cleaned name (without Set of ##)
    const cleanedName = cleanProductName(item.name);
    const weightMatch = cleanedName.match(/(\d+\.?\d*)\s*(g|kg|oz|lb|ml)/i);
    if (weightMatch) {
      return `${weightMatch[1]}${weightMatch[2].toLowerCase()}`;
    }
    
    return '';
  };

  const totalWeight = order.items.reduce((sum, item) => sum + getItemWeight(item), 0);

  const getJamaicanPrice = (item: typeof order.items[0]): number | null => {
    const product = productData[item.id];
    if (!product || typeof product.jamaicanPrice !== 'number') {
      return null;
    }
    return normalizeCurrencyValue(product.jamaicanPrice);
  };

  const itemPricing = order.items.map((item, index) => {
    const originalUnitPriceUSD = item.onSale && item.onSalePrice ? item.onSalePrice : item.price;
    const setQuantity = extractSetQuantity(item.name);

    if (isJamaicanPricing) {
      const jamaicanPrice = getJamaicanPrice(item);
      if (jamaicanPrice === null) {
        return { unitPrice: null, subtotal: null };
      }

      const displayUnitPrice = setQuantity > 1
        ? normalizeCurrencyValue(jamaicanPrice / setQuantity)
        : jamaicanPrice;
      const subtotal = normalizeCurrencyValue(jamaicanPrice * item.quantity);
      return { unitPrice: displayUnitPrice, subtotal };
    }

    const overridePrice = convertedPrices?.[index];
    const convertedPrice = normalizeCurrencyValue(overridePrice ?? getConvertedItemPriceFromOrder(originalUnitPriceUSD));
    const displayUnitPrice = setQuantity > 1
      ? normalizeCurrencyValue(convertedPrice / setQuantity)
      : convertedPrice;
    const subtotal = normalizeCurrencyValue(convertedPrice * item.quantity);
    return { unitPrice: displayUnitPrice, subtotal };
  });

  const hasMissingPrices = isJamaicanPricing && itemPricing.some((pricing) => pricing.unitPrice === null);
  const convertedTotalValue = hasMissingPrices
    ? null
    : Number(itemPricing.reduce((sum, pricing) => sum + (pricing.subtotal || 0), 0).toFixed(2));

  const receiverAddress = order.shipping?.address || 
                          (order.customer.addresses && order.customer.addresses.length > 0 
                            ? order.customer.addresses[0] 
                            : null);
  
  const receiverAddressLine1 = receiverAddress?.line1 || order.customer.address?.line1 || '';
  const receiverAddressLine2 = receiverAddress?.line2 || order.customer.address?.line2 || '';
  const receiverCity = receiverAddress?.city || order.customer.address?.city || '';
  const receiverState = receiverAddress?.state || order.customer.address?.state || '';
  const receiverZipCode = receiverAddress?.zipCode || receiverAddress?.['postal_code'] || order.customer.address?.postal_code || '';
  const receiverCountry = receiverAddress?.country || order.customer.address?.country || '';
  const receiverPhone = receiverAddress?.phoneNumber || order.customer.phone || '';
  
  const currency = getCurrency();
  const totalValueLabel = convertedTotalValue === null ? '-' : formatCurrency(convertedTotalValue, currency);

  return (
    <Document>
      <Page size={{ width: 288, height: 432 }} style={styles.page}>
        {/* Header Section */}
        <View style={styles.header}>
          <Text style={styles.title}>COMMERCIAL INVOICE</Text>
          <View style={styles.invoiceInfo}>
            <Text>
              <Text style={styles.infoLabel}>Invoice No:</Text> {invoiceNumber}
            </Text>
            <Text>
              <Text style={styles.infoLabel}>Date:</Text> {formatDate(new Date())}
            </Text>
          </View>
          <View style={styles.invoiceInfo}>
            <Text>
              <Text style={styles.infoLabel}>AWB Number:</Text> {awbNumber}
            </Text>
            <Text>
              <Text style={styles.infoLabel}>Order:</Text> {order.orderNumber}
            </Text>
          </View>
        </View>

        {/* Shipper & Receiver Section */}
        <View style={styles.partiesSection} wrap={true}>
          <View style={styles.partyBox}>
            <Text style={styles.partyTitle}>SHIPPER (FROM)</Text>
            <Text style={styles.partyText}>{shipperInfo.company}</Text>
            <Text style={styles.partyText}>{shipperInfo.name}</Text>
            <Text style={styles.partyText}>{shipperInfo.address}</Text>
            <Text style={styles.partyText}>
              {shipperInfo.city}, {shipperInfo.state} {shipperInfo.zipCode}
            </Text>
            <Text style={styles.partyText}>{shipperInfo.country}</Text>
            <Text style={styles.partyText}>Tel: {shipperInfo.phone}</Text>
            {shipperInfo.email && (
              <Text style={styles.partyText}>Email: {shipperInfo.email}</Text>
            )}
            <Text style={styles.partyText}>TRN: 129554391/1</Text>
          </View>

          <View style={styles.partyBox}>
            <Text style={styles.partyTitle}>RECEIVER (TO)</Text>
            <Text style={styles.partyText}>{receiverAddress?.fullName || order.customer.name}</Text>
            {receiverAddressLine1 && (
              <Text style={styles.partyText}>{receiverAddressLine1}</Text>
            )}
            {receiverAddressLine2 && (
              <Text style={styles.partyText}>{receiverAddressLine2}</Text>
            )}
            {(receiverCity || receiverState || receiverZipCode) && (
              <Text style={styles.partyText}>
                {receiverCity}{receiverCity && receiverState ? ', ' : ''}{receiverState} {receiverZipCode}
              </Text>
            )}
            {receiverCountry && (
              <Text style={styles.partyText}>{receiverCountry}</Text>
            )}
            {receiverPhone && (
              <Text style={styles.partyText}>Tel: {receiverPhone}</Text>
            )}
            {order.customer.email && (
              <Text style={styles.partyText}>Email: {order.customer.email}</Text>
            )}
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table} wrap={true} minPresenceAhead={0}>
          <View style={styles.tableHeader} wrap={false} fixed>
            <View style={styles.colDescription}>
              <Text style={styles.tableHeaderText}>Description</Text>
            </View>
            <View style={styles.colHsCode}>
              <Text style={styles.tableHeaderText}>HS Code</Text>
            </View>
            <View style={styles.colOrigin}>
              <Text style={styles.tableHeaderText}>COO</Text>
            </View>
            <View style={styles.colQty}>
              <Text style={styles.tableHeaderText}>Qty</Text>
            </View>
            <View style={styles.colUnitValue}>
              <Text style={styles.tableHeaderText}>Unit{'\n'}Value</Text>
            </View>
            <View style={styles.colSubtotal}>
              <Text style={styles.tableHeaderText}>Subtotal</Text>
            </View>
            <View style={styles.colCombinedWeight}>
              <Text style={styles.tableHeaderText}>Net/Gross{'\n'}Wgt (kg)</Text>
            </View>
          </View>

          {order.items.map((item, index) => {
            const product = productData[item.id];
            const effectiveQuantity = getEffectiveQuantity(item);
            const pricing = itemPricing[index];
            const displayUnitPrice = pricing?.unitPrice ?? null;
            const subtotal = pricing?.subtotal ?? null;
            const weight = getItemWeight(item);
            const brand = getBrandName(item);
            const productWeight = getProductWeight(item);
            const cleanedName = cleanProductName(item.name);
            
            // Get HS Code and Country of Origin from product data (like CSV export does)
            const hsCode = product?.hsCode || item.hsCode || '-';
            const originCountry = product?.originCountry || item.originCountry || 'JM';

            return (
              <View key={index} style={styles.tableRow} wrap={false}>
                <View style={styles.colDescription}>
                  <Text style={styles.tableCell}>
                    {brand}{'\n'}
                    {cleanedName} {productWeight}
                  </Text>
                </View>
                <View style={styles.colHsCode}>
                  <Text style={styles.tableCell}>{hsCode}</Text>
                </View>
                <View style={styles.colOrigin}>
                  <Text style={styles.tableCell}>{originCountry}</Text>
                </View>
                <View style={styles.colQty}>
                  <Text style={styles.tableCell}>{effectiveQuantity}</Text>
                </View>
                <View style={styles.colUnitValue}>
                  <Text style={styles.tableCell}>
                    {displayUnitPrice === null ? '-' : `${displayUnitPrice.toFixed(2)}\n${currency}`}
                  </Text>
                </View>
                <View style={styles.colSubtotal}>
                  <Text style={styles.tableCell}>
                    {subtotal === null ? '-' : `${subtotal.toFixed(2)}\n${currency}`}
                  </Text>
                </View>
                <View style={styles.colCombinedWeight}>
                  <Text style={styles.tableCell}>
                    {weight.toFixed(3)} / {weight.toFixed(3)}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>

        {/* Summary Section */}
        <View style={styles.summarySection} wrap={false}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Goods Value:</Text>
            <Text style={styles.summaryValue}>{totalValueLabel}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Invoice Amount:</Text>
            <Text style={styles.summaryValue}>{totalValueLabel}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Net Weight:</Text>
            <Text style={styles.summaryValue}>{totalWeight.toFixed(3)} kg</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Gross Weight:</Text>
            <Text style={styles.summaryValue}>{totalWeight.toFixed(3)} kg</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Terms of Trade (Incoterm):</Text>
            <Text style={styles.summaryValue}>DAP (Delivered At Place)</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Reason for Export:</Text>
            <Text style={styles.summaryValue}>Sale of Goods</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Type of Export:</Text>
            <Text style={styles.summaryValue}>Commercial</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Currency Code:</Text>
            <Text style={styles.summaryValue}>{currency}</Text>
          </View>
        </View>

        {/* Declaration Section */}
        <View style={styles.declarationSection} wrap={false}>
          <Text style={styles.declarationTitle}>CUSTOMS DECLARATION</Text>
          <Text style={styles.declarationText}>
            I hereby certify that the information contained in this invoice is true and correct, and that the contents and value of this shipment is as stated above.
          </Text>

          <View style={{ marginTop: 6, flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={styles.tableCell}>Name: {shipperInfo.name}</Text>
            <Text style={styles.tableCell}>Date: {formatDate(new Date())}</Text>
          </View>

          <View style={{ marginTop: 6, flexDirection: 'row', alignItems: 'center' }}>
            <Text style={styles.tableCell}>Signature: </Text>
            <Image 
              src="/lovable-uploads/cleaned_signature.png" 
              style={{ 
                width: 75, 
                height: 28,
                objectFit: 'contain',
                marginLeft: 4
              }} 
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};
